import React, { useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from 'components/common/Button/Button';
import { Table } from 'components/common/Table/Table';
import { InvitePatientModal } from 'components/patient/InvitePatientModal/InvitePatientModal';
import { QueryKeys } from 'constants/query-keys';
import { useMutationWithToast } from 'hooks/useMutationWithToast';
import { InviteService } from 'services/invite';
import { getReadableDate } from 'utils/common';

export function InvitedPatients() {
  const queryClient = useQueryClient();
  const [openInvitePatientModal, setOpenInvitePatientModal] = useState(false);
  const [resendInvite, setResendInvite] = useState<
    { id: string; email: string } | undefined
  >();
  const [page, setPage] = useState<number>(1);
  const getInvitedPatientsQuery = useQuery(
    QueryKeys.InvitedPatients.listing({ page }),
    () => InviteService.getInvitedPatients({ page })
  );

  const deleteInviteMutation = useMutationWithToast(
    InviteService.deletePatientInvite
  );

  function deleteInvite(id: string) {
    deleteInviteMutation.mutate(
      { id },
      {
        onSuccess: () => {
          queryClient.refetchQueries(QueryKeys.InvitedPatients.listings());
        },
      }
    );
  }

  return (
    <div className="grid">
      <span className="mb-3 ml-auto flex items-center justify-between text-base">
        <Button
          className="ml-auto"
          onClick={() => setOpenInvitePatientModal(true)}>
          Invite Patient
        </Button>
      </span>
      <Table
        loading={getInvitedPatientsQuery?.isLoading}
        rowsData={getInvitedPatientsQuery?.data?.data?.items || []}
        columns={[
          {
            title: 'Id',
            render: (rowData) => rowData.id,
          },
          {
            title: 'Email',
            render: (rowData) => rowData.email,
          },
          {
            title: 'Date',
            render: (rowData) => getReadableDate(rowData.createdAt),
            classNames: 'text-center',
          },
          {
            title: '',
            render: (rowData) => (
              <div className="flex justify-end">
                <Button
                  className="!text-base"
                  size="small"
                  onClick={() => {
                    setOpenInvitePatientModal(true);
                    setResendInvite({ id: rowData.id, email: rowData.email });
                  }}>
                  Resend
                </Button>
                <span className="mx-2" />
                <Button
                  className="!text-base"
                  loading={
                    deleteInviteMutation.isLoading &&
                    deleteInviteMutation.variables?.id === rowData.id
                  }
                  size="small"
                  variant="contained"
                  color="error"
                  onClick={() => deleteInvite(rowData.id)}>
                  Delete
                </Button>
              </div>
            ),
          },
        ]}
        noDataMessage="You currently have no invited patients"
        pagination
        onPageChange={setPage}
        page={page}
        totalRecords={getInvitedPatientsQuery?.data?.data.total || 0}
        recordsPerPage={10}
      />

      <InvitePatientModal
        openModal={openInvitePatientModal}
        resendInviteData={resendInvite}
        onModalClose={() => {
          setOpenInvitePatientModal(false);
          setTimeout(() => setResendInvite(undefined), 300);
        }}
        onInviteResendSuccess={() => {
          setOpenInvitePatientModal(false);
          setTimeout(() => setResendInvite(undefined), 300);
        }}
      />
    </div>
  );
}
